import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";
import {
    createManagerDriver,
    createManagerSeasonDriver,
    deleteManagerDriver,
    deleteManagerSeasonDriver,
    fetchManagerDrivers,
    fetchManagerSeriesSeasons,
    fetchManagerTeams,
    updateManagerDriver,
    updateManagerSeasonDriver,
    uploadManagerDriversCsv,
} from "../../lib/managerApi.js";

const STATUS_OPTIONS = [
    { value: "active", label: "Aktiv" },
    { value: "inactive", label: "Inaktiv" },
];

const STATUS_FILTER_OPTIONS = [
    { value: "all", label: "Alle Fahrer" },
    { value: "active", label: "Nur aktiv" },
    { value: "inactive", label: "Nur inaktiv" },
];

const CLASS_OPTIONS = [
    { value: "PRO", label: "PRO" },
    { value: "INT", label: "INT" },
    { value: "AM", label: "AM" },
];

const NEW_TEAM_OPTION_VALUE = "__new_team__";

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function driverName(driver) {
    return driver?.display_name || driver?.iracing_name || driver?.label || "Fahrer";
}

function seasonDriverLabel(driver) {
    const sd = driver?.season_driver || driver?.season_entries?.[0];
    const number = sd?.car_number ? `#${sd.car_number}` : "";
    return [number, driverName(driver)].filter(Boolean).join(" ") || "Fahrer";
}

function discordLabel(user) {
    if (!user) return "Nicht verknuepft";
    return user.display_name || user.discord_global_name || user.discord_username || `Discord #${user.id}`;
}

function statusLabel(status) {
    return status === "inactive" ? "Inaktiv" : "Aktiv";
}

function normalizeTeamName(value) {
    return String(value || "").trim().toLowerCase();
}

function formatWaitlistPosition(gridPosition, maxRegularStarters = 60) {
    const position = Number(gridPosition || 0);
    const maxRegular = Number(maxRegularStarters || 60);
    if (!position || position <= maxRegular) return "";
    return `W${position}`;
}

function parseWaitlistPositionInput(value) {
    const raw = String(value || "").trim();
    if (!raw) return { value: undefined, error: "" };

    const match = raw.toUpperCase().match(/^W?\s*(\d{1,4})$/);
    if (!match) {
        return { value: undefined, error: "Wartelisteposition bitte als W61 eingeben." };
    }

    const parsed = Number(match[1]);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return { value: undefined, error: "Wartelisteposition ist ungueltig." };
    }

    return { value: parsed, error: "" };
}

function StatusPill({ status }) {
    const inactive = status === "inactive";

    return (
        <span className={inactive ? "pill pill--danger" : "pill pill--am"}>
            {statusLabel(status)}
        </span>
    );
}

function Field({ label, children }) {
    return (
        <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 800, fontSize: 13 }}>{label}</span>
            {children}
        </label>
    );
}

export default function DriverManagement() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasonId, setSeasonId] = useState("all");
    const [seasonDriverSeasonId, setSeasonDriverSeasonId] = useState("");
    const [search, setSearch] = useState("");
    const [driverStatus, setDriverStatus] = useState("all");

    const [drivers, setDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [creatingDriver, setCreatingDriver] = useState(false);
    const [selectedSeason, setSelectedSeason] = useState(null);

    const [driverForm, setDriverForm] = useState({
        iracing_name: "",
        iracing_id: "",
        display_name: "",
        stream_url: "",
        status: "active",
    });
    const [seasonForm, setSeasonForm] = useState({
        waitlist_position: "",
        car_number: "",
        class_name: "",
        team_name: "",
        status: "active",
    });
    const [teamOptions, setTeamOptions] = useState([]);
    const [teamOptionsLoading, setTeamOptionsLoading] = useState(false);
    const [forceNewTeamInput, setForceNewTeamInput] = useState(false);

    const [importFile, setImportFile] = useState(null);
    const [importSeasonId, setImportSeasonId] = useState("");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const selectedDriver = useMemo(() => {
        return drivers.find((driver) => String(driver.id) === String(selectedDriverId)) || null;
    }, [drivers, selectedDriverId]);

    const selectedSeries = useMemo(() => {
        return seriesRows.find((series) => series.series_key === seriesKey) || null;
    }, [seriesRows, seriesKey]);

    const seasonEditOptions = useMemo(() => {
        return (selectedSeries?.seasons || []).map((season) => ({
            value: season.id,
            label: `${season.display_name} (${season.status})`,
        }));
    }, [selectedSeries]);

    const seasonFilterOptions = useMemo(() => {
        return [
            { value: "all", label: "Alle Fahrer" },
            ...seasonEditOptions,
        ];
    }, [seasonEditOptions]);

    const seriesOptions = useMemo(() => {
        return seriesRows.map((series) => ({
            value: series.series_key,
            label: series.display_name,
        }));
    }, [seriesRows]);

    const selectedSeasonForEdit = useMemo(() => {
        return selectedSeries?.seasons?.find((season) => String(season.id) === String(seasonDriverSeasonId)) || null;
    }, [selectedSeries, seasonDriverSeasonId]);

    const selectedSeasonEntry = useMemo(() => {
        return selectedDriver?.season_entries?.find((entry) => String(entry.season_id) === String(seasonDriverSeasonId)) || null;
    }, [selectedDriver, seasonDriverSeasonId]);

    const selectedTeamLogoUrl = useMemo(() => {
        return (
            selectedSeasonEntry?.team?.logo_url ||
            selectedDriver?.season_driver?.team?.logo_url ||
            selectedSeasonEntry?.team_logo_url ||
            selectedDriver?.season_driver?.team_logo_url ||
            ""
        );
    }, [selectedSeasonEntry, selectedDriver]);

    const maxRegularStartersForEdit = Number(selectedSeasonForEdit?.max_regular_starters || 60);

    const existingTeamOptions = useMemo(() => {
        const unique = new Map();
        for (const teamName of teamOptions) {
            const label = String(teamName || "").trim();
            if (!label) continue;
            const key = normalizeTeamName(label);
            if (!unique.has(key)) unique.set(key, label);
        }

        return Array.from(unique.values())
            .sort((a, b) => a.localeCompare(b, "de"))
            .map((name) => ({ value: name, label: name }));
    }, [teamOptions]);

    const teamSelectOptions = useMemo(() => {
        return [
            ...existingTeamOptions,
            { value: NEW_TEAM_OPTION_VALUE, label: "Neues Team anlegen ..." },
        ];
    }, [existingTeamOptions]);

    const derivedTeamSelectValue = useMemo(() => {
        const teamName = String(seasonForm.team_name || "").trim();
        if (!teamName) return "";
        const normalized = normalizeTeamName(teamName);
        const existing = existingTeamOptions.find((option) => normalizeTeamName(option.value) === normalized);
        return existing ? existing.value : NEW_TEAM_OPTION_VALUE;
    }, [seasonForm.team_name, existingTeamOptions]);

    const teamSelectValue = forceNewTeamInput ? NEW_TEAM_OPTION_VALUE : derivedTeamSelectValue;
    const showNewTeamInput = forceNewTeamInput || derivedTeamSelectValue === NEW_TEAM_OPTION_VALUE;

    useEffect(() => {
        let active = true;

        async function loadSeries() {
            try {
                setLoading(true);
                setError("");
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const nextSeries = Array.isArray(data.series) ? data.series : [];
                setSeriesRows(nextSeries);

                const preferred = nextSeries.find((series) => series.series_key === "gt3x") || nextSeries[0];
                if (preferred) {
                    setSeriesKey((current) => current || preferred.series_key);
                    const activeSeason =
                        preferred.seasons?.find((season) => season.status === "active") ||
                        preferred.seasons?.[0];
                    setSeasonId(activeSeason ? String(activeSeason.id) : "all");
                    if (activeSeason) {
                        setSeasonDriverSeasonId(String(activeSeason.id));
                        setImportSeasonId(String(activeSeason.id));
                    }
                }
            } catch (err) {
                if (active) setError(prettyError(err));
            } finally {
                if (active) setLoading(false);
            }
        }

        loadSeries();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const series = seriesRows.find((row) => row.series_key === seriesKey);
        if (!series) return;

        const hasCurrentSeason = seasonId === "all" || series.seasons?.some((season) => String(season.id) === String(seasonId));
        const hasEditSeason = series.seasons?.some((season) => String(season.id) === String(seasonDriverSeasonId));

        const activeSeason =
            series.seasons?.find((season) => season.status === "active") ||
            series.seasons?.[0];
        if (!hasCurrentSeason) setSeasonId(activeSeason ? String(activeSeason.id) : "all");
        if (!hasEditSeason) setSeasonDriverSeasonId(activeSeason ? String(activeSeason.id) : "");
        setImportSeasonId((current) => current || (activeSeason ? String(activeSeason.id) : ""));
    }, [seriesRows, seriesKey, seasonId, seasonDriverSeasonId]);

    async function loadDrivers() {
        if (!seriesKey) return;

        try {
            setLoading(true);
            setError("");
            const allSeasons = seasonId === "all";
            const data = await fetchManagerDrivers({
                series_key: seriesKey,
                season_id: allSeasons ? "" : seasonId,
                all_seasons: allSeasons ? "true" : "",
                include_all_drivers: allSeasons ? "true" : "",
                search,
                driver_status: driverStatus,
            });

            const rows = Array.isArray(data.drivers) ? data.drivers : [];
            setDrivers(rows);
            setSelectedSeason(data.season || null);

            setSelectedDriverId((current) => {
                if (current && rows.some((driver) => String(driver.id) === String(current))) return current;
                if (creatingDriver) return "";
                return rows[0]?.id ? String(rows[0].id) : "";
            });
        } catch (err) {
            setError(prettyError(err));
            setDrivers([]);
            setSelectedDriverId("");
        } finally {
            setLoading(false);
        }
    }

    async function loadTeamOptions() {
        if (!seriesKey || !seasonDriverSeasonId) {
            setTeamOptions([]);
            return;
        }

        try {
            setTeamOptionsLoading(true);
            const data = await fetchManagerTeams({
                series_key: seriesKey,
                season_id: seasonDriverSeasonId,
                all_seasons: "false",
                team_status: "all",
            });
            const groups = Array.isArray(data.teams) ? data.teams : [];
            const names = [];

            for (const group of groups) {
                const groupName = String(group?.display_name || "").trim();
                if (groupName) names.push(groupName);

                for (const entry of group?.season_entries || []) {
                    const entryName = String(entry?.display_name || entry?.team_name || "").trim();
                    if (entryName) names.push(entryName);
                }
            }

            setTeamOptions(names);
        } catch (_err) {
            setTeamOptions([]);
        } finally {
            setTeamOptionsLoading(false);
        }
    }

    useEffect(() => {
        if (!seriesKey) return;
        loadDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey, seasonId, driverStatus]);

    useEffect(() => {
        loadTeamOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey, seasonDriverSeasonId]);

    useEffect(() => {
        if (!selectedDriver) {
            setDriverForm({
                iracing_name: "",
                iracing_id: "",
                display_name: "",
                stream_url: "",
                status: "active",
            });
            setForceNewTeamInput(false);
            setSeasonForm({
                waitlist_position: "",
                car_number: "",
                class_name: "",
                team_name: "",
                status: "active",
            });
            return;
        }

        setDriverForm({
            iracing_name: selectedDriver.iracing_name || "",
            iracing_id: selectedDriver.iracing_id || "",
            display_name: selectedDriver.display_name || "",
            stream_url: selectedDriver.stream_url || "",
            status: selectedDriver.status || "active",
        });

        setSeasonForm({
            waitlist_position: formatWaitlistPosition(selectedSeasonEntry?.grid_position, maxRegularStartersForEdit),
            car_number: selectedSeasonEntry?.car_number || "",
            class_name: selectedSeasonEntry?.class_name || "",
            team_name: selectedSeasonEntry?.team_name || "",
            status: selectedSeasonEntry?.status || "active",
        });
        setForceNewTeamInput(false);
    }, [selectedDriver, selectedSeasonEntry, maxRegularStartersForEdit]);

    function patchDriverInList(nextDriver) {
        if (!nextDriver?.id) return;
        setDrivers((prev) => prev.map((driver) => (
            String(driver.id) === String(nextDriver.id)
                ? {
                    ...driver,
                    ...nextDriver,
                    season_driver: nextDriver.season_driver || driver.season_driver,
                }
                : driver
        )));
    }

    function startCreateDriver() {
        setCreatingDriver(true);
        setSelectedDriverId("");
        setDriverForm({
            iracing_name: "",
            iracing_id: "",
            display_name: "",
            stream_url: "",
            status: "active",
        });
        setSeasonForm({
            waitlist_position: "",
            car_number: "",
            class_name: "",
            team_name: "",
            status: "active",
        });
        setForceNewTeamInput(false);
        setSuccess("");
        setError("");
    }

    async function saveDriver() {
        if (!driverForm.iracing_name?.trim()) {
            setError("Bitte iRacing Name eintragen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            if (creatingDriver) {
                const data = await createManagerDriver({
                    ...driverForm,
                    series_key: seriesKey,
                });
                setCreatingDriver(false);
                setSelectedDriverId(data?.driver?.id ? String(data.driver.id) : "");
                await loadDrivers();
                setSuccess("Fahrer wurde angelegt.");
            } else {
                if (!selectedDriver) return;
                const data = await updateManagerDriver(selectedDriver.id, {
                    ...driverForm,
                    series_key: seriesKey,
                });
                patchDriverInList(data.driver);
                setSuccess("Fahrer wurde gespeichert.");
            }
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function removeDriver() {
        if (!selectedDriver?.id) return;
        const name = driverName(selectedDriver);
        if (!window.confirm(`Fahrer '${name}' wirklich loeschen?`)) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await deleteManagerDriver(selectedDriver.id, { series_key: seriesKey });
            setSuccess("Fahrer wurde geloescht.");
            setSelectedDriverId("");
            await loadDrivers();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function saveSeasonDriver() {
        if (!selectedSeasonEntry?.id) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const parsedWaitlist = parseWaitlistPositionInput(seasonForm.waitlist_position);
            if (parsedWaitlist.error) {
                setError(parsedWaitlist.error);
                return;
            }
            if (parsedWaitlist.value && parsedWaitlist.value <= maxRegularStartersForEdit) {
                setError(`Wartelisteposition muss groesser als ${maxRegularStartersForEdit} sein.`);
                return;
            }
            const normalizedTeamName = String(seasonForm.team_name || "").trim();
            const data = await updateManagerSeasonDriver(selectedSeasonEntry.id, {
                car_number: seasonForm.car_number,
                class_name: seasonForm.class_name,
                status: seasonForm.status,
                grid_position: parsedWaitlist.value,
                team_name: normalizedTeamName,
            });
            patchDriverInList(data.driver);
            await loadTeamOptions();
            setSuccess("Saisonfahrer wurde gespeichert.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function removeSeasonDriver() {
        if (!selectedSeasonEntry?.id) return;
        if (!window.confirm("Diesen Fahrer aus der ausgewaehlten Saison entfernen?")) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            await deleteManagerSeasonDriver(selectedSeasonEntry.id);
            setSuccess("Fahrer wurde aus der Saison entfernt.");
            await loadDrivers();
            await loadTeamOptions();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function assignSeasonDriver() {
        if (!selectedDriver || !selectedSeasonForEdit?.id) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const parsedWaitlist = parseWaitlistPositionInput(seasonForm.waitlist_position);
            if (parsedWaitlist.error) {
                setError(parsedWaitlist.error);
                return;
            }
            if (parsedWaitlist.value && parsedWaitlist.value <= maxRegularStartersForEdit) {
                setError(`Wartelisteposition muss groesser als ${maxRegularStartersForEdit} sein.`);
                return;
            }
            const normalizedTeamName = String(seasonForm.team_name || "").trim();
            const data = await createManagerSeasonDriver({
                season_id: Number(selectedSeasonForEdit.id),
                driver_id: Number(selectedDriver.id),
                grid_position: parsedWaitlist.value,
                car_number: seasonForm.car_number,
                class_name: seasonForm.class_name,
                team_name: normalizedTeamName,
                status: seasonForm.status || "active",
            });
            patchDriverInList(data.driver);
            await loadTeamOptions();
            setSuccess("Fahrer wurde der Saison zugeordnet.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function uploadCsv() {
        if (!importFile) {
            setError("Bitte eine CSV-Datei auswaehlen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await uploadManagerDriversCsv({
                file: importFile,
                seriesKey,
                seasonId: importSeasonId === "none" ? "" : importSeasonId,
            });

            const summary = data.summary || {};
            const errorCount = Array.isArray(summary.errors) ? summary.errors.length : 0;
            setSuccess(
                `CSV importiert: Fahrer neu ${summary.driver_created || 0}, Fahrer aktualisiert ${summary.driver_updated || 0}, ` +
                `Saisonfahrer neu ${summary.season_driver_created || 0}, Saisonfahrer aktualisiert ${summary.season_driver_updated || 0}` +
                (errorCount ? `. Fehlerhafte Zeilen: ${errorCount}` : ".")
            );
            await loadDrivers();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Liga Manager</div>
                        <h1 className="rv-page-title">Fahrerverwaltung</h1>
                        <p className="rv-page-subtitle">
                            Fahrer suchen, Stammdaten pflegen und Saisonfahrer fuer die aktive Liga bearbeiten.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                        gap: 12,
                    }}
                >
                    <Field label="Serie">
                        <CustomSelect
                            value={seriesKey}
                            onChange={setSeriesKey}
                            options={seriesOptions}
                            allowEmpty={false}
                        />
                    </Field>

                    <Field label="Saison">
                        <CustomSelect
                            value={seasonId}
                            onChange={setSeasonId}
                            options={seasonFilterOptions}
                            allowEmpty={false}
                            disabled={!seasonFilterOptions.length}
                        />
                    </Field>

                    <Field label="Fahrerstatus">
                        <CustomSelect
                            value={driverStatus}
                            onChange={setDriverStatus}
                            options={STATUS_FILTER_OPTIONS}
                            allowEmpty={false}
                        />
                    </Field>

                    <Field label="Suche">
                        <input
                            className="input"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") loadDrivers();
                            }}
                            placeholder="Name, iRacing ID ..."
                        />
                    </Field>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <button className="btn btn--accent" type="button" onClick={loadDrivers} disabled={loading}>
                        {loading ? "Lade ..." : "Fahrer laden"}
                    </button>
                    <button className="btn" type="button" onClick={startCreateDriver} disabled={busy}>
                        Neuer Fahrer
                    </button>
                    {creatingDriver && (
                        <button className="btn" type="button" onClick={() => setCreatingDriver(false)} disabled={busy}>
                            Abbrechen
                        </button>
                    )}
                    {selectedSeason && (
                        <span className="muted">
                            Aktuelle Auswahl: {selectedSeason.display_name} ({selectedSeason.status})
                        </span>
                    )}
                    {!selectedSeason && seasonId === "all" && (
                        <span className="muted">
                            Aktuelle Auswahl: Alle Fahrer dieser Serie
                        </span>
                    )}
                </div>
            </div>

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div>
                    <h2 style={{ marginTop: 0 }}>CSV Import</h2>
                    <p className="muted" style={{ marginTop: 4 }}>
                        CSV aus Google Sheets hochladen. Ohne Saison werden nur Fahrer-Stammdaten importiert;
                        mit Saison werden zusaetzlich Eintraege in rv_season_drivers angelegt oder aktualisiert.
                    </p>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                        gap: 12,
                        alignItems: "end",
                    }}
                >
                    <Field label="CSV Datei">
                        <input
                            className="input"
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                        />
                    </Field>

                    <Field label="Saison fuer Zuordnung">
                        <CustomSelect
                            value={importSeasonId || "none"}
                            onChange={setImportSeasonId}
                            options={[
                                { value: "none", label: "Nur Fahrer-Stammdaten" },
                                ...seasonEditOptions,
                            ]}
                            allowEmpty={false}
                        />
                    </Field>

                    <button className="btn btn--accent" type="button" onClick={uploadCsv} disabled={busy || !importFile}>
                        CSV importieren
                    </button>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                <div className="card" style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <h2 style={{ marginTop: 0 }}>Fahrer</h2>
                        <span className="pill pill--gesamt">{drivers.length}</span>
                    </div>

                    <div style={{ display: "grid", gap: 8, maxHeight: 720, overflow: "auto", paddingRight: 4 }}>
                        {!drivers.length && (
                            <div className="muted">Keine Fahrer gefunden.</div>
                        )}

                        {drivers.map((driver) => {
                            const active = String(driver.id) === String(selectedDriverId);
                            const seasonText = seasonId === "all"
                                ? (
                                    driver.season_entries?.length
                                        ? driver.season_entries
                                            .map((entry) => {
                                                const waitlist = formatWaitlistPosition(entry.grid_position, entry.season?.max_regular_starters);
                                                return `${entry.season?.display_name || "Saison"}: ${entry.class_name || "Klasse offen"}${waitlist ? ` / ${waitlist}` : ""}`;
                                            })
                                            .join(" | ")
                                        : "Keiner Saison zugeordnet"
                                )
                                : (
                                    driver.season_driver
                                        ? `${driver.season_driver.class_name || "Klasse offen"}${
                                            formatWaitlistPosition(driver.season_driver.grid_position, selectedSeason?.max_regular_starters)
                                                ? ` / ${formatWaitlistPosition(driver.season_driver.grid_position, selectedSeason?.max_regular_starters)}`
                                                : ""
                                        }`
                                        : "Nicht in dieser Saison"
                                );

                            return (
                                <button
                                    key={driver.id}
                                    type="button"
                                    onClick={() => {
                                        setCreatingDriver(false);
                                        setSelectedDriverId(String(driver.id));
                                    }}
                                    style={{
                                        textAlign: "left",
                                        border: active
                                            ? "1px solid rgba(228,254,60,.46)"
                                            : "1px solid rgba(255,255,255,.10)",
                                        background: active
                                            ? "rgba(228,254,60,.10)"
                                            : "rgba(255,255,255,.035)",
                                        borderRadius: 14,
                                        padding: 12,
                                        color: "var(--text)",
                                        cursor: "pointer",
                                        display: "grid",
                                        gap: 6,
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                        <strong>{seasonDriverLabel(driver)}</strong>
                                        <StatusPill status={driver.status} />
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        {seasonText}
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        Discord: {discordLabel(driver.discord_user)}
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        iRacing ID: {driver.iracing_id || "-"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="card" style={{ display: "grid", gap: 18 }}>
                    {!selectedDriver && !creatingDriver ? (
                        <div className="muted">Bitte einen Fahrer auswaehlen.</div>
                    ) : (
                        <>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    gap: 14,
                                }}
                            >
                                <div style={{ minWidth: 0 }}>
                                    <div className="rv-page-kicker">
                                        {creatingDriver ? "Neuer Fahrer" : "Bearbeiten"}
                                    </div>

                                    <h2
                                        style={{
                                            margin: "4px 0 0",
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {creatingDriver ? "Fahrer anlegen" : seasonDriverLabel(selectedDriver)}
                                    </h2>

                                    {!creatingDriver && selectedDriver && (
                                        <p className="muted" style={{ marginTop: 6 }}>
                                            ID {selectedDriver.id} / Discord: {discordLabel(selectedDriver.discord_user)}
                                        </p>
                                    )}
                                </div>

                                {!creatingDriver && selectedTeamLogoUrl && (
                                    <div
                                        style={{
                                            width: 98,
                                            height: 72,
                                            borderRadius: 16,
                                            border: "1px solid rgba(255,255,255,.10)",
                                            background: "rgba(0,0,0,.16)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 10,
                                            flex: "0 0 auto",
                                        }}
                                    >
                                        <img
                                            src={selectedTeamLogoUrl}
                                            alt="Teamlogo"
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                                display: "block",
                                            }}
                                            loading="lazy"
                                        />
                                    </div>
                                )}
                            </div>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Fahrer-Stammdaten</h3>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                        gap: 12,
                                    }}
                                >
                                    <Field label="iRacing Name">
                                        <input
                                            className="input"
                                            value={driverForm.iracing_name}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, iracing_name: event.target.value }))}
                                        />
                                    </Field>

                                    <Field label="iRacing ID">
                                        <input
                                            className="input"
                                            value={driverForm.iracing_id}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, iracing_id: event.target.value }))}
                                            placeholder="Optional"
                                        />
                                    </Field>

                                    <Field label="Klarname / Anzeige">
                                        <input
                                            className="input"
                                            value={driverForm.display_name}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, display_name: event.target.value }))}
                                            placeholder="Optional"
                                        />
                                    </Field>

                                    <Field label="Status">
                                        <CustomSelect
                                            value={driverForm.status}
                                            onChange={(value) => setDriverForm((prev) => ({ ...prev, status: value }))}
                                            options={STATUS_OPTIONS}
                                            allowEmpty={false}
                                        />
                                    </Field>

                                    <Field label="Stream Link">
                                        <input
                                            className="input"
                                            value={driverForm.stream_url}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, stream_url: event.target.value }))}
                                            placeholder="https://twitch.tv/... oder https://youtube.com/..."
                                        />
                                    </Field>
                                </div>

                                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                    <button className="btn btn--accent" type="button" onClick={saveDriver} disabled={busy}>
                                        {creatingDriver ? "Fahrer anlegen" : "Fahrer speichern"}
                                    </button>
                                    {!creatingDriver && selectedDriver && (
                                        <button className="btn" type="button" onClick={removeDriver} disabled={busy}>
                                            Fahrer loeschen
                                        </button>
                                    )}
                                </div>
                            </section>

                            {creatingDriver ? (
                                <section style={{ display: "grid", gap: 12 }}>
                                    <h3 style={{ margin: 0 }}>Saisonfahrer</h3>
                                    <div className="muted">
                                        Saisonfahrer-Daten koennen gespeichert werden, sobald der neue Fahrer angelegt wurde.
                                    </div>
                                </section>
                            ) : (
                                <section style={{ display: "grid", gap: 12 }}>
                                    <h3 style={{ margin: 0 }}>Saisonfahrer</h3>

                                <Field label="Saison fuer Saisonfahrer-Daten">
                                    <CustomSelect
                                        value={seasonDriverSeasonId}
                                        onChange={setSeasonDriverSeasonId}
                                        options={seasonEditOptions}
                                        allowEmpty={false}
                                        disabled={!seasonEditOptions.length}
                                    />
                                </Field>

                                {selectedSeasonEntry && (
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        Saisonauto (nur Info): {selectedSeasonEntry.season_car_name || "Noch nicht festgelegt"}
                                        {selectedSeasonEntry.season_car_locked ? " / gesperrt" : ""}
                                    </div>
                                )}

                                {!selectedSeasonEntry ? (
                                    <>
                                        <div className="rv-message rv-message--error">
                                            Dieser Fahrer ist der ausgewaehlten Saison noch nicht zugeordnet.
                                            Trage die Saisonfahrer-Daten ein und ordne ihn dieser Saison zu.
                                        </div>

                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                                                gap: 12,
                                            }}
                                        >
                                            <Field label="Wartelisteposition">
                                                <input
                                                    className="input"
                                                    value={seasonForm.waitlist_position}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, waitlist_position: event.target.value }))}
                                                    placeholder={`Optional, z. B. W${maxRegularStartersForEdit + 1}`}
                                                />
                                            </Field>

                                            <Field label="Startnummer">
                                                <input
                                                    className="input"
                                                    value={seasonForm.car_number}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, car_number: event.target.value }))}
                                                />
                                            </Field>

                                            <Field label="Klasse">
                                                <CustomSelect
                                                    value={seasonForm.class_name}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, class_name: value }))}
                                                    options={CLASS_OPTIONS}
                                                />
                                            </Field>

                                            <Field label="Saisonstatus">
                                                <CustomSelect
                                                    value={seasonForm.status}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, status: value }))}
                                                    options={STATUS_OPTIONS}
                                                    allowEmpty={false}
                                                />
                                            </Field>
                                        </div>

                                        <Field label="Team">
                                            <div style={{ display: "grid", gap: 8 }}>
                                                <CustomSelect
                                                    value={teamSelectValue}
                                                    onChange={(value) => {
                                                        if (value === NEW_TEAM_OPTION_VALUE) {
                                                            setForceNewTeamInput(true);
                                                            setSeasonForm((prev) => {
                                                                const hasExisting = existingTeamOptions.some(
                                                                    (option) => normalizeTeamName(option.value) === normalizeTeamName(prev.team_name)
                                                                );
                                                                return { ...prev, team_name: hasExisting ? "" : prev.team_name };
                                                            });
                                                            return;
                                                        }
                                                        setForceNewTeamInput(false);
                                                        setSeasonForm((prev) => ({ ...prev, team_name: value }));
                                                    }}
                                                    options={teamSelectOptions}
                                                    placeholder={teamOptionsLoading ? "Teams laden ..." : "Kein Team"}
                                                />
                                                {showNewTeamInput && (
                                                    <>
                                                        <input
                                                            className="input"
                                                            value={seasonForm.team_name}
                                                            onChange={(event) => setSeasonForm((prev) => ({ ...prev, team_name: event.target.value }))}
                                                            placeholder="Neuen Teamnamen eingeben"
                                                        />
                                                        <span className="muted" style={{ fontSize: 12 }}>
                                                            Wird beim Speichern automatisch als Team angelegt, falls noch nicht vorhanden.
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </Field>

                                        <div>
                                            <button className="btn btn--accent" type="button" onClick={assignSeasonDriver} disabled={busy || !selectedSeasonForEdit?.id}>
                                                Fahrer dieser Saison zuordnen
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                                                gap: 12,
                                            }}
                                        >
                                            <Field label="Wartelisteposition">
                                                <input
                                                    className="input"
                                                    value={seasonForm.waitlist_position}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, waitlist_position: event.target.value }))}
                                                    placeholder={`Optional, z. B. W${maxRegularStartersForEdit + 1}`}
                                                />
                                            </Field>

                                            <Field label="Startnummer">
                                                <input
                                                    className="input"
                                                    value={seasonForm.car_number}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, car_number: event.target.value }))}
                                                />
                                            </Field>

                                            <Field label="Klasse">
                                                <CustomSelect
                                                    value={seasonForm.class_name}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, class_name: value }))}
                                                    options={CLASS_OPTIONS}
                                                />
                                            </Field>

                                            <Field label="Saisonstatus">
                                                <CustomSelect
                                                    value={seasonForm.status}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, status: value }))}
                                                    options={STATUS_OPTIONS}
                                                    allowEmpty={false}
                                                />
                                            </Field>
                                        </div>

                                        <Field label="Team">
                                            <div style={{ display: "grid", gap: 8 }}>
                                                <CustomSelect
                                                    value={teamSelectValue}
                                                    onChange={(value) => {
                                                        if (value === NEW_TEAM_OPTION_VALUE) {
                                                            setForceNewTeamInput(true);
                                                            setSeasonForm((prev) => {
                                                                const hasExisting = existingTeamOptions.some(
                                                                    (option) => normalizeTeamName(option.value) === normalizeTeamName(prev.team_name)
                                                                );
                                                                return { ...prev, team_name: hasExisting ? "" : prev.team_name };
                                                            });
                                                            return;
                                                        }
                                                        setForceNewTeamInput(false);
                                                        setSeasonForm((prev) => ({ ...prev, team_name: value }));
                                                    }}
                                                    options={teamSelectOptions}
                                                    placeholder={teamOptionsLoading ? "Teams laden ..." : "Kein Team"}
                                                />
                                                {showNewTeamInput && (
                                                    <>
                                                        <input
                                                            className="input"
                                                            value={seasonForm.team_name}
                                                            onChange={(event) => setSeasonForm((prev) => ({ ...prev, team_name: event.target.value }))}
                                                            placeholder="Neuen Teamnamen eingeben"
                                                        />
                                                        <span className="muted" style={{ fontSize: 12 }}>
                                                            Wird beim Speichern automatisch als Team angelegt, falls noch nicht vorhanden.
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </Field>

                                        <div>
                                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                                <button className="btn btn--accent" type="button" onClick={saveSeasonDriver} disabled={busy}>
                                                    Saisonfahrer speichern
                                                </button>
                                                <button className="btn" type="button" onClick={removeSeasonDriver} disabled={busy}>
                                                    Von Saison entfernen
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
